// Stable Router that can be used in useEffect
// https://github.com/vercel/next.js/issues/18127#issuecomment-1619721701
import Router, { useRouter } from 'next/router';
import {
  Dispatch, SetStateAction, useEffect, useRef, useMemo,
} from 'react';
import { buildSearchParams, buildObjectFromSearchParams, firstQueryValue } from '@utils/data-fetching/searchParams';
import { ObjectFilterParams } from '@utils/infinite-scrolling/processedServerSideQueries';
import { ParsedUrlQuery } from 'querystring';
import { getJobListParametersFromSlug, buildJobListUrl } from '@utils/job';
import { TFunction } from 'next-i18next';
import { useRouterLocale } from '@hooks/common/useRouterLocale';
import { InfinitePageType } from '@type/swr';
import { SAVE_ALERT_PARAM } from 'src/constants';
import { extractDynamicRouteParams } from './extractDynamicRouteParams';


// Next.js-injected slug segments that are not meaningful filter values.
const RESERVED_SLUG_SEGMENTS = new Set(['job', 'list']);

// Locale-specific job-list path segments for detecting non-shallow route changes.
const JOB_LIST_PATH_SEGMENTS = [
  '/job/list',
  '/locuri-de-munca',
  '/jobs',
  '/allasok',
  '/stellenangebote',
  '/emplois',
  '/lavori',
  '/trabajos',
];

const getBaseQueryKey = (key: string) => key.replace(/\[.*$/, '');

// Params that ride the internal href of a navigation and must not be echoed into the address bar: the
// "foreign param" preservation below would otherwise put them there on the next filter change, and a
// reloaded or shared URL would replay them.
const INTERNAL_QUERY_PARAMS = new Set<string>([SAVE_ALERT_PARAM]);


/**
 * Job-list-specific URL and history hook (Pages Router). Extends `useBrowserHistoryAndURL` with
 * locale-aware SEO slug URLs, slug parsing on non-shallow route changes, and initial tracking-param
 * preservation. For all other paginated lists use `useBrowserHistoryAndURL`.
 */
export const useJobListHistoryAndURL = (
  pageInView: string | number | undefined,
  searchParams: ObjectFilterParams | object | undefined,
  setSearchParams: Dispatch<SetStateAction<object>>,
  t: TFunction,
  paginationType: InfinitePageType = 'page',
) => {
  const router = useRouter();
  const lastSearchParamsRef = useRef<string | null>('');
  // Tracks all keys ever in searchParams so removed-filter params aren't treated as "foreign" params.
  const allManagedKeysRef = useRef<Set<string>>(new Set(Object.keys(searchParams || {}).map(getBaseQueryKey)));

  const locale = useRouterLocale();

  // Dynamic route param names — must not appear in the query string.
  const dynamicRouteParams = useMemo(
    () => extractDynamicRouteParams(router.pathname),
    [router.pathname],
  );

  useEffect(() => {
    const { pageNr: pageNrQuery, ...otherQueryParams } = router.query;

    const searchParamsKeys = new Set(Object.keys(searchParams || {}));
    searchParamsKeys.forEach((k) => allManagedKeysRef.current.add(getBaseQueryKey(k)));

    // Extra params from router.query: not dynamic route segments, not filter keys.
    // Strip bracket suffixes (e.g. 'careerLevels[]' → 'careerLevels') — Router.push with a query string
    // causes router.query to contain encoded bracket-keyed entries.
    const routerExtraParams: ParsedUrlQuery = {};
    Object.entries(otherQueryParams).forEach(([key, value]) => {
      const baseKey = getBaseQueryKey(key);
      if (!dynamicRouteParams.has(key) && !allManagedKeysRef.current.has(baseKey) && !INTERNAL_QUERY_PARAMS.has(baseKey)) {
        routerExtraParams[key] = value;
      }
    });

    // Preserve "foreign" params from window.location.search (e.g. selectedJobSlug set by the modal
    // via native history API) — Next.js router never sees them and would silently drop them.
    const liveSearchParams = new URLSearchParams(window.location.search);
    const actualQueryParams: ParsedUrlQuery = { ...routerExtraParams };
    liveSearchParams.forEach((value, key) => {
      const baseKey = getBaseQueryKey(key);
      if (
        key !== 'pageNr'
        && !dynamicRouteParams.has(key)
        && !allManagedKeysRef.current.has(baseKey)
        && !INTERNAL_QUERY_PARAMS.has(baseKey)
        && !(key in routerExtraParams)
      ) {
        actualQueryParams[key] = value;
      }
    });

    let pageNrQueryValue: string | number = paginationType === 'cursor' ? '' : 1;
    if (paginationType === 'cursor') {
      pageNrQueryValue = Array.isArray(pageNrQuery) ? pageNrQuery[0] : (pageNrQuery ?? '');
    } else if (Array.isArray(pageNrQuery)) {
      pageNrQueryValue = parseInt(pageNrQuery[0], 10);
    } else if (typeof pageNrQuery !== 'undefined') {
      pageNrQueryValue = parseInt(pageNrQuery, 10);
    }

    const urlParams = buildSearchParams(searchParams, '');

    // Filter change → shallow push with SEO slug URL; preserve tracking params on first push.
    if (urlParams !== lastSearchParamsRef.current) {
      const newQueryWithAllParams: ParsedUrlQuery = { ...actualQueryParams };
      if (typeof pageNrQuery !== 'undefined' && !dynamicRouteParams.has('pageNr')) {
        newQueryWithAllParams.pageNr = pageNrQuery;
      }

      const newQuery = `${urlParams}${buildSearchParams(newQueryWithAllParams, '&')}`;
      lastSearchParamsRef.current = urlParams;

      // Build the display URL: SEO slug path + foreign params (modal slugs, utm, etc.).
      const foreignQuery = buildSearchParams(actualQueryParams, '?').replace(/%5B/gi, '[').replace(/%5D/gi, ']');
      const jobListUrl = buildJobListUrl(t, locale, searchParams) + foreignQuery;

      // Keep href/as path-compatible to avoid Next.js "incompatible href/as" runtime errors on dynamic routes.
      const [jobListPathname] = jobListUrl.split('?');
      const hrefUrl = newQuery ? `${jobListPathname}?${newQuery}` : jobListPathname;
      void Router.push(hrefUrl, jobListUrl, { shallow: true, scroll: false });
    }

    // Page / cursor change → replaceState to avoid React re-render (infinite-scroll reset).
    if (
      (pageInView && paginationType === 'page' && (pageInView !== pageNrQueryValue || (pageInView === 1 && typeof pageNrQuery !== 'undefined')))
      || (paginationType === 'cursor' && pageInView !== pageNrQueryValue)
    ) {
      const newQueryWithPageNr: ParsedUrlQuery = { ...actualQueryParams };

      if (paginationType === 'cursor') {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      } else if (Number(pageInView) > 1) {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      }

      const newQueryString = `${urlParams}${buildSearchParams(newQueryWithPageNr, '&')}`;
      const decodedQueryString = newQueryString.replace(/%5B/gi, '[').replace(/%5D/gi, ']');
      const newUrl = decodedQueryString ? `${window.location.pathname}?${decodedQueryString}` : window.location.pathname;

      window.history.replaceState(
        { ...window.history.state, as: newUrl, url: newUrl },
        '',
        newUrl,
      );
    }
  }, [pageInView, searchParams, router, t, locale, paginationType, dynamicRouteParams]);


  // Restore filter state on back/forward navigation.
  useEffect(() => {
    router.beforePopState(({ as }) => {
      const currentPath = router.asPath;
      if (as !== currentPath) {
        const currentParamsObj = buildObjectFromSearchParams(window.location.search) || {};
        setSearchParams(currentParamsObj);
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router, setSearchParams]);


  // Non-shallow route change (e.g. external link with a different slug) → parse slug into filter params.
  useEffect(() => {
    const handleRouteChangeComplete = (url: string, { shallow }: { shallow: boolean }) => {
      if (shallow) return;

      const isJobListPage = JOB_LIST_PATH_SEGMENTS.some((segment) => url.includes(segment));
      if (!isJobListPage) return;

      const rawSlug = router.query.slug;
      const cleanedSlug = Array.isArray(rawSlug)
        ? rawSlug.filter((segment) => !RESERVED_SLUG_SEGMENTS.has(segment))
        : rawSlug;

      const newSearchParams = getJobListParametersFromSlug(cleanedSlug, t);

      // A search submitted by the user puts the keyword as typed on the internal href of its navigation,
      // so prefer that over the slug: the slug is lower-cased, stripped of diacritics and can't tell its
      // own separators from hyphens the user typed. Absent (a link access, a reload) the slug stands.
      const keywordFromQuery = firstQueryValue(router.query.keyword);
      if (keywordFromQuery) {
        newSearchParams.keyword = keywordFromQuery;
      }

      lastSearchParamsRef.current = buildSearchParams(newSearchParams, '');
      setSearchParams(newSearchParams);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events, router.query, setSearchParams, t]);
};
